import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import TopBar from "@/components/TopBar";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { PauseCircle, CreditCard, AlertTriangle } from "lucide-react";

// FASE 20 — while suspended, only the financial area stays reachable
const SUSPENDED_ALLOWED = ["/billing", "/pricing", "/support"];

function SuspendedScreen({ isAdmin }) {
  const navigate = useNavigate();
  return (
    <div className="flex-1 flex items-center justify-center p-6">
      <Card className="max-w-lg w-full p-8 rounded-2xl border-rose-200 bg-white text-center" data-testid="suspended-screen">
        <div className="h-14 w-14 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mx-auto mb-4">
          <PauseCircle className="h-7 w-7" />
        </div>
        <h2 className="font-display text-2xl font-bold tracking-tight text-stone-950">
          Assinatura suspensa
        </h2>
        <p className="text-sm text-stone-600 font-body mt-2">
          O acesso da sua empresa foi suspenso. Entre em contato com a gestão do
          sistema para verificar qualquer pendência financeira e restabelecer os
          serviços.
        </p>
        <div className="mt-6 flex gap-2 justify-center flex-wrap">
          {isAdmin && (
            <Button
              onClick={() => navigate("/billing")}
              className="bg-emerald-800 hover:bg-emerald-900 text-white"
              data-testid="suspended-go-billing"
            >
              <CreditCard className="h-4 w-4 mr-2" /> Ir para Planos & Cobrança
            </Button>
          )}
          <Button variant="outline" onClick={() => navigate("/support")} data-testid="suspended-go-support">
            Falar com o suporte
          </Button>
        </div>
      </Card>
    </div>
  );
}

export default function Layout({ title, subtitle, actions, children }) {
  const { user, company, impersonation } = useAuth();
  const location = useLocation();

  const isSuper = user?.role === "super_admin";
  const suspended =
    company?.status === "suspended" && !isSuper && !impersonation;
  const routeAllowed = SUSPENDED_ALLOWED.some((p) => location.pathname.startsWith(p));
  const blocked = suspended && !routeAllowed;
  const showWarning = company?.billing_warning && !suspended && !isSuper;

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <ImpersonationBanner />
        {showWarning && (
          <div
            data-testid="billing-warning-banner"
            className="bg-amber-500 text-white text-center text-xs sm:text-sm font-semibold py-2 px-4 flex items-center justify-center gap-2"
          >
            <AlertTriangle className="h-4 w-4 shrink-0" />
            Aviso de atraso: há pendências financeiras na sua assinatura. Regularize
            em Planos &amp; Cobrança para evitar a suspensão.
          </div>
        )}
        <TopBar title={blocked ? "Assinatura suspensa" : title} subtitle={blocked ? "" : subtitle} actions={blocked ? null : actions} />
        {blocked ? (
          <SuspendedScreen isAdmin={user?.role === "company_admin"} />
        ) : (
          <main className="flex-1 px-4 sm:px-6 py-6">{children}</main>
        )}
      </div>
    </div>
  );
}
